import { useMemo } from 'react'

import { useApolloClient } from '@apollo/client'

import {
  EmployeeJobCatalogRolesFilterDocument,
  EmployeeJobCatalogTreeLevelsFilterDocument,
  type EmployeeJobCatalogTreeLevelsFilterQuery,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

type Params = {
  hasJobCatalogTreeFeature: boolean
}

type FilterOption = {
  label: string
  value: string
  children?: {
    filterKey: 'jobRoleLevel'
    options: Array<{ label: string; value: string }>
  }
}

type TreeLevelNode = NonNullable<
  EmployeeJobCatalogTreeLevelsFilterQuery['jobCatalog']['treeLevelsConnection']
>['nodes'][number]

const getTreeOptions = (
  treeLevels?: TreeLevelNode[] | null
): Array<{ label: string; value: string }> =>
  (treeLevels ?? []).flatMap((treeLevel) => {
    const roleName = treeLevel.ancestor?.name
    const levelName = treeLevel.name
    const levelUuid = treeLevel.uuid

    if (!roleName || !levelName) {
      return []
    }

    return [
      {
        label: `${roleName} / ${levelName}`,
        value: levelUuid,
      },
    ]
  })

const getTreeGroupedOptions = (treeLevels?: TreeLevelNode[] | null): FilterOption[] => {
  const roleOptions = new Map<string, FilterOption>()

  for (const treeLevel of treeLevels ?? []) {
    const roleUuid = treeLevel.ancestor?.uuid
    const roleName = treeLevel.ancestor?.name
    const levelName = treeLevel.name

    if (!roleUuid || !roleName || !levelName) {
      continue
    }

    const currentRole = roleOptions.get(roleUuid)

    if (currentRole && currentRole.children) {
      currentRole.children.options.push({
        label: levelName,
        value: treeLevel.uuid,
      })

      continue
    }

    roleOptions.set(roleUuid, {
      label: roleName,
      value: roleUuid,
      children: {
        filterKey: 'jobRoleLevel',
        options: [
          {
            label: levelName,
            value: treeLevel.uuid,
          },
        ],
      },
    })
  }

  return [...roleOptions.values()]
}

export const getFilterOptionLabel = (options: FilterOption[], value: unknown) => {
  for (const option of options) {
    if (option.value === value) return option.label

    const childOption = option.children?.options.find((child) => child.value === value)
    if (childOption) return `${option.label} > ${childOption.label}`
  }

  return String(value)
}

const createTreeOptionsLoader = (client: ReturnType<typeof useApolloClient>) => {
  let treeOptionsPromise: Promise<FilterOption[]> | undefined

  return () => {
    treeOptionsPromise ??= client
      .query({
        query: EmployeeJobCatalogTreeLevelsFilterDocument,
        fetchPolicy: 'cache-first',
      })
      .then(({ data }) => getTreeGroupedOptions(data.jobCatalog.treeLevelsConnection?.nodes))

    return treeOptionsPromise
  }
}

const getRoleOptions = (rolesData?: {
  jobCatalog: {
    roles: Array<{
      name: string
      levels: Array<{ id: string | number; isDefault: boolean; name: string }>
    }>
  }
}) =>
  rolesData?.jobCatalog.roles.flatMap((role) =>
    role.levels.map((level) => ({
      label: level.isDefault ? role.name : `${role.name} / ${level.name}`,
      value: String(level.id),
    }))
  ) ?? []

export const useJobCatalogRoleOptions = ({ hasJobCatalogTreeFeature }: Params) => {
  const { data: treeData } = useQuery(EmployeeJobCatalogTreeLevelsFilterDocument, {
    skip: !hasJobCatalogTreeFeature,
  })

  const { data: rolesData } = useQuery(EmployeeJobCatalogRolesFilterDocument, {
    skip: hasJobCatalogTreeFeature,
  })

  return useMemo(() => {
    if (hasJobCatalogTreeFeature) {
      return getTreeOptions(treeData?.jobCatalog.treeLevelsConnection?.nodes)
    }

    return getRoleOptions(rolesData)
  }, [hasJobCatalogTreeFeature, rolesData, treeData])
}

export const useJobCatalogRoleFilter = ({ hasJobCatalogTreeFeature }: Params) => {
  const client = useApolloClient()

  return useMemo(() => {
    const getTreeOptions = createTreeOptionsLoader(client)

    return {
      type: 'in' as const,
      label: i18n.t('employees.employees_list.filters.job_role'),
      options: {
        options: async () => {
          if (hasJobCatalogTreeFeature) return getTreeOptions()

          const { data } = await client.query({
            query: EmployeeJobCatalogRolesFilterDocument,
            fetchPolicy: 'cache-first',
          })

          return getRoleOptions(data)
        },
        getLabel: async (value: unknown) => {
          if (!hasJobCatalogTreeFeature) return String(value)

          return getFilterOptionLabel(await getTreeOptions(), value)
        },
      },
    }
  }, [client, hasJobCatalogTreeFeature])
}

export const useJobCatalogRoleLevelFilter = ({ hasJobCatalogTreeFeature }: Params) => {
  const client = useApolloClient()

  return useMemo(() => {
    const getTreeOptions = createTreeOptionsLoader(client)

    return {
      label: i18n.t('employees.employees_list.filters.job_role'),
      hideSelector: true,
      type: 'in' as const,
      options: {
        options: [],
        getLabel: async (value: unknown) => {
          if (!hasJobCatalogTreeFeature) return String(value)

          return getFilterOptionLabel(await getTreeOptions(), value)
        },
      },
    }
  }, [client, hasJobCatalogTreeFeature])
}
